import { ApiError, apiFetch } from "./api";

export type ReferralErrorKind =
	| "invalid-format"
	| "reserved"
	| "already-set"
	| "already-taken"
	| "insufficient-balance"
	| "hyperliquid-unreachable"
	| "unauthorized"
	| "unknown";

export interface CreateReferralResponse {
	code: string;
}

export interface LookupReferralResponse {
	code: string;
}

export interface ReferredTrader {
	wallet: string;
	joined_at: string;
	volume_usd: string;
	earned_usd: string;
}

export interface ReferralStats {
	code: string | null;
	claimable_usd: string;
	lifetime_earned_usd: string;
	referred_count: number;
	referred_traders: ReferredTrader[];
	revenue_share_bps?: number;
}

export type WithdrawalStatus = "pending" | "sent" | "confirmed" | "failed";

export interface WithdrawalResponse {
	withdrawal_id: string;
	amount_usd: string;
	to_address: string;
	chain: string;
	status: WithdrawalStatus;
	created_at: string;
}

export interface WithdrawalRow {
	id: string;
	amount_usd: string;
	to_address: string;
	chain: string;
	status: WithdrawalStatus;
	tx_hash?: string | null;
	created_at: string;
	broadcast_at?: string | null;
	confirmed_at?: string | null;
	error?: string | null;
}

export interface WithdrawalsListResponse {
	withdrawals: WithdrawalRow[];
}

export type WithdrawErrorKind =
	| "below-minimum"
	| "inflight"
	| "unauthorized"
	| "unknown";

/**
 * Sweep the caller's entire claimable balance into a pending withdrawal.
 * No amount parameter — the server takes everything (above the configured
 * minimum) and sends to the user's authenticated wallet on Arbitrum.
 */
export function withdrawReferrals(): Promise<WithdrawalResponse> {
	return apiFetch<WithdrawalResponse>("/v1/referrals/me/withdraw", {
		method: "POST",
	});
}

export function fetchMyWithdrawals(): Promise<WithdrawalsListResponse> {
	return apiFetch<WithdrawalsListResponse>("/v1/referrals/me/withdrawals", {
		method: "GET",
	});
}

/**
 * Classify the backend's withdraw errors. The server uses an HTTP-403 with
 * a body `status` of 0 for "below minimum" and `status: 409` for "inflight",
 * so we have to look at the parsed payload, not just the HTTP code.
 */
export function classifyWithdrawError(err: unknown): WithdrawErrorKind {
	if (!(err instanceof ApiError)) return "unknown";
	if (err.status === 401) return "unauthorized";
	if (err.status === 403) {
		const payload = err.payload as { status?: number; error?: string } | null;
		if (payload?.status === 409) return "inflight";
		const msg = (payload?.error ?? "").toLowerCase();
		if (msg.includes("in progress")) return "inflight";
		if (msg.includes("minimum")) return "below-minimum";
		return "below-minimum";
	}
	return "unknown";
}

export function createReferralCode(
	code: string,
): Promise<CreateReferralResponse> {
	return apiFetch<CreateReferralResponse>("/v1/referrals", {
		method: "POST",
		body: { code },
	});
}

export function fetchMyReferralStats(): Promise<ReferralStats> {
	return apiFetch<ReferralStats>("/v1/referrals/me/stats", { method: "GET" });
}

/**
 * Public lookup — validates a shared referral code exists.
 * Returns the canonical uppercase form on 200, or null on 400/404.
 */
export async function lookupReferralCode(
	code: string,
): Promise<string | null> {
	const encoded = encodeURIComponent(code);
	try {
		const res = await apiFetch<LookupReferralResponse>(
			`/v1/referrals/${encoded}`,
			{ method: "GET" },
		);
		return res.code;
	} catch (err) {
		if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
			return null;
		}
		throw err;
	}
}

export function classifyReferralError(err: unknown): ReferralErrorKind {
	if (!(err instanceof ApiError)) return "unknown";

	const payload = err.payload as { status?: number; error?: string } | null;
	const bodyStatus = payload?.status;
	const msg = (payload?.error ?? err.message).toLowerCase();

	if (bodyStatus === 400) {
		if (msg.includes("reserved")) return "reserved";
		return "invalid-format";
	}
	if (bodyStatus === 409) {
		if (msg.includes("already set")) return "already-set";
		if (msg.includes("already taken")) return "already-taken";
		return "unknown";
	}
	if (bodyStatus === 403) return "insufficient-balance";
	if (bodyStatus === 502) return "hyperliquid-unreachable";
	if (bodyStatus === 401) return "unauthorized";
	return "unknown";
}
